import React, { useState, useEffect } from "react";
import axios from "axios";
import { Html5QrcodeScanner } from "html5-qrcode";
import Header from "../../component/Header";
import Sidebar from "../../component/Sidebar";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

const ScannerPage = () => {
  const [scannedCode, setScannedCode] = useState("");
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const userId = localStorage.getItem("token");

  useEffect(() => {
    let scanner;
    // Initialize html5-qrcode scanner
    scanner = new Html5QrcodeScanner(
      "reader",
      { fps: 10, qrbox: { width: 250, height: 150 } },
      /* verbose= */ false
    );
    scanner.render(onScanSuccess, onScanFailure);

    function onScanSuccess(decodedText, decodedResult) {
      if (decodedText) {
        setScannedCode(decodedText);
        fetchProduct(decodedText);
        scanner.pause(true); // pause camera after scan
      }
    }

    function onScanFailure(error) {
      // handle scan failure, usually better to ignore and keep scanning
    }

    return () => {
      scanner.clear().catch(error => console.error("Failed to clear html5QrcodeScanner. ", error));
    };
  }, []);

  const fetchProduct = async (code) => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/productByBarcode/${code}`);
      setProduct(res.data);
      setEditData({
        price: res.data.Price || "",
        seller: res.data.seller || "",
        receiver: res.data.receiver || "",
        ourStat: res.data.ourStat || "",
      });
      toast.success("Product found!");
    } catch (err) {
      setProduct(null);
      toast.error("Product not found by this barcode.");
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (scannedCode) {
      fetchProduct(scannedCode);
    }
  };

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (field, value, endpoint) => {
    try {
      setLoading(true);
      let payload = { id: product.ID };
      
      if (endpoint === "updateItem") {
         // for price we might just want to call the updateItem endpoint which needs form data
         // but wait, Admin uses specific endpoints for single stats like updateOurStat, updateSeller, updateReceiver
      }
      
      if (field === "ourStat") {
        await axios.put(`${apiUrl}/updateOurStat`, { id: product.ID, ourStat: value }, { headers: { Authorization: `Bearer ${userId}` } });
      } else if (field === "seller") {
        await axios.put(`${apiUrl}/updateSeller`, { id: product.ID, seller: value }, { headers: { Authorization: `Bearer ${userId}` } });
      } else if (field === "receiver") {
        await axios.put(`${apiUrl}/updateReceiver`, { id: product.ID, receiver: value }, { headers: { Authorization: `Bearer ${userId}` } });
      } else if (field === "price") {
        // Price update can be tricky, let's create a specific one or use updating form data
        const formData = new FormData();
        formData.append("price", value);
        await axios.put(`${apiUrl}/updateItem/${product.ID}`, formData, { headers: { Authorization: `Bearer ${userId}` } });
      }
      toast.success(`${field} updated successfully!`);
      // Re-fetch to confirm
      fetchProduct(scannedCode);
    } catch (err) {
      toast.error(`Failed to update ${field}`);
    } finally {
      setLoading(false);
    }
  };

  const resumeScan = () => {
    setProduct(null);
    setScannedCode("");
    // The scanner will auto-resume if unmounted/remounted or we can just reload
    window.location.reload(); 
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 font-sans">
      <Header />
      <Sidebar />
      <div className="flex-1 flex flex-col items-center py-10 px-4 md:ml-64 mt-16 transition-all duration-300">
        
        <div className="bg-white p-6 rounded-2xl shadow-xl shadow-slate-200/50 w-full max-w-2xl border border-slate-100">
          <h1 className="text-3xl font-extrabold text-slate-800 mb-6 flex items-center gap-3">
             <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm14 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
             Barcode Scanner
          </h1>
          
          {!product ? (
            <div className="flex flex-col items-center">
              <div id="reader" className="w-full max-w-sm rounded-xl overflow-hidden border-2 border-dashed border-indigo-200 shadow-inner"></div>
              
              <div className="mt-8 w-full">
                <div className="flex items-center text-sm text-slate-400 mb-4 px-4 before:flex-1 before:border-t before:border-slate-200 before:mr-4 after:flex-1 after:border-t after:border-slate-200 after:ml-4">OR ENTER MANUAL BARCODE</div>
                <form onSubmit={handleManualSearch} className="flex gap-2">
                  <input
                    type="text"
                    value={scannedCode}
                    onChange={(e) => setScannedCode(e.target.value)}
                    placeholder="Scan with gun or type..."
                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                    autoFocus
                  />
                  <button type="submit" disabled={loading} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-lg shadow-indigo-200 transition-all active:scale-95 disabled:opacity-70">
                    {loading ? "Searching..." : "Search"}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="space-y-6 animate-fade-in">
               <div className="bg-indigo-50/50 p-6 rounded-xl border border-indigo-100 flex gap-6 items-start">
                  <div className="flex-1">
                     <p className="text-sm font-semibold text-indigo-500 uppercase tracking-widest mb-1">Product Found</p>
                     <h2 className="text-2xl font-bold text-slate-800">{product.Brand} {product.Model}</h2>
                     <p className="text-slate-500">{product.Storage} | {product.Ram} | {product.Sim}</p>
                     <p className="inline-block mt-3 px-3 py-1 bg-white border border-slate-200 text-xs font-mono rounded-lg shadow-sm text-slate-600">Order: {product.OrderNumber}</p>
                  </div>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                 
                 {/*  Status Editor */}
                 <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Store Stat</label>
                    <div className="flex gap-2">
                       <select name="ourStat" value={editData.ourStat} onChange={handleChange} className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium">
                          <option value="Unpaid">Unpaid</option>
                          <option value="Sold">Sold</option>
                          <option value="Received">Received</option>
                          <option value="Sent">Sent</option>
                          <option value="Returned">Returned</option>
                          <option value="Repair">Repair</option>
                          <option value="None">None</option>
                       </select>
                       <button onClick={() => handleUpdate('ourStat', editData.ourStat)} className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition-colors">Save</button>
                    </div>
                 </div>

                 {/* Seller Editor */}
                 <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Seller</label>
                    <div className="flex gap-2">
                       <input type="text" name="seller" value={editData.seller} onChange={handleChange} placeholder="Seller Name" className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                       <button onClick={() => handleUpdate('seller', editData.seller)} className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition-colors">Save</button>
                    </div>
                 </div>

                 {/* Receiver Editor */}
                 <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Receiver</label>
                    <div className="flex gap-2">
                       <input type="text" name="receiver" value={editData.receiver} onChange={handleChange} placeholder="Receiver Name" className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                       <button onClick={() => handleUpdate('receiver', editData.receiver)} className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition-colors">Save</button>
                    </div>
                 </div>

                 {/* Price Editor */}
                 <div className="p-4 rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Price (ETB)</label>
                    <div className="flex gap-2">
                       <input type="number" name="price" value={editData.price} onChange={handleChange} placeholder="Price" className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium" />
                       <button onClick={() => handleUpdate('price', editData.price)} className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition-colors">Save</button>
                    </div>
                 </div>

               </div>
               
               <div className="pt-6 border-t border-slate-100 flex justify-end">
                  <button onClick={resumeScan} className="px-6 py-3 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl font-semibold transition-colors flex items-center gap-2">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>
                     Scan Another
                  </button>
               </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ScannerPage;
